import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'
import type {
  LockerRoomPublicConfig,
  LockerRoomVisibility,
  LockerRoomQuote,
  LockerRoomWishlistItem,
  LockerRoomTopMoment,
  LockerRoomTopMomentsConfig,
} from '@/types/locker-rooms'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

const defaultVisibility: LockerRoomVisibility = {
  isPublic: true,
  showShareLink: true,
  showInCommunityRotation: false,
}

const defaultTopMoments: LockerRoomTopMomentsConfig = {
  maxSlots: 5,
  items: [],
}

const defaultConfig: LockerRoomPublicConfig = {
  displayName: '',
  about: '',
  quote: undefined,
  goals: [],
  wishlist: [],
  theme: { style: 'electric' },
  topMoments: defaultTopMoments,
  visibility: defaultVisibility,
  hero: { headline: '', subheadline: '' },
}

const THEME_OPTIONS = [
  { value: 'electric', label: 'Electric', desc: 'Amber & fuchsia gradients with indigo accents' },
  { value: 'earth', label: 'Earth', desc: 'Forest greens with sand and amber highlights' },
  { value: 'midnight', label: 'Midnight', desc: 'Deep indigo with amber accents for bold contrast' },
  { value: 'clean', label: 'Clean', desc: 'Crisp blues and whites for a professional look' },
]

const MOMENT_SLOT_OPTIONS = [3, 5, 7, 10] as const

interface LockerRoom {
  id: string
  name: string
  public_name: string | null
  public_config: LockerRoomPublicConfig | null
  space_code: string | null
}

export default function LockerRoomPublicPage() {
  const { user, session } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [lockerRoom, setLockerRoom] = useState<LockerRoom | null>(null)
  const [config, setConfig] = useState<LockerRoomPublicConfig>(defaultConfig)
  const [publicName, setPublicName] = useState('')

  const publicPageUrl = useMemo(() => {
    if (!lockerRoom?.space_code) return null
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://projecttower.org'
    return `${base}/locker/${lockerRoom.space_code}`
  }, [lockerRoom?.space_code])

  useEffect(() => {
    if (!user?.id) return

    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data, error: err } = await supabase
          .from('locker_rooms')
          .select('id, name, public_name, public_config, space_code')
          .eq('coach_user_id', user.id)
          .is('archived_at', null)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (err) throw err
        if (!data) {
          setError('No locker room found for your account.')
          return
        }

        const room = data as LockerRoom
        setLockerRoom(room)
        setPublicName(room.public_name || room.name || '')

        const raw = (room.public_config || {}) as LockerRoomPublicConfig
        setConfig({
          ...defaultConfig,
          ...raw,
          goals: Array.isArray(raw.goals) ? raw.goals : [],
          wishlist: Array.isArray(raw.wishlist) ? raw.wishlist : [],
          theme: { ...defaultConfig.theme, ...(raw.theme || {}) },
          topMoments: {
            ...defaultTopMoments,
            ...(raw.topMoments || {}),
            items: Array.isArray(raw.topMoments?.items) ? raw.topMoments!.items : [],
          },
          visibility: { ...defaultVisibility, ...(raw.visibility || {}) },
          hero: { ...defaultConfig.hero, ...(raw.hero || {}) },
        })
      } catch (err: any) {
        setError(err?.message || 'Failed to load locker room')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [user?.id])

  const handleSave = useCallback(async () => {
    if (!lockerRoom?.id || !session?.access_token) return
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const res = await fetch(`${CORE_API}/api/v1/locker-room/public-config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          locker_room_id: lockerRoom.id,
          public_name: publicName.trim() || lockerRoom.name || 'Locker Room',
          ...config,
        }),
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error?.message || body?.error || 'Failed to save')
      }

      setSuccess('Public page updated!')
      setTimeout(() => setSuccess(null), 4000)
    } catch (err: any) {
      setError(err?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }, [lockerRoom, session, publicName, config])

  const visibility = config.visibility ?? defaultVisibility

  const handleVisibilityChange = (key: keyof LockerRoomVisibility, value: boolean) => {
    setConfig((prev) => ({
      ...prev,
      visibility: { ...defaultVisibility, ...(prev.visibility || {}), [key]: value },
    }))
  }

  const handleQuoteChange = (patch: Partial<LockerRoomQuote>) => {
    setConfig((prev) => ({
      ...prev,
      quote: { text: prev.quote?.text ?? '', ...prev.quote, ...patch },
    }))
  }

  const handleWishlistChange = (idx: number, patch: Partial<LockerRoomWishlistItem>) => {
    setConfig((prev) => {
      const next = [...(prev.wishlist || [])]
      next[idx] = { ...next[idx], ...patch } as LockerRoomWishlistItem
      return { ...prev, wishlist: next }
    })
  }

  const handleMomentChange = (idx: number, patch: Partial<LockerRoomTopMoment>) => {
    setConfig((prev) => {
      const items = [...(prev.topMoments?.items || [])]
      items[idx] = { ...items[idx], ...patch }
      return { ...prev, topMoments: { ...(prev.topMoments || defaultTopMoments), items } }
    })
  }

  if (loading) return <div className="p-6 text-cocoa/60">Loading...</div>
  if (!lockerRoom) return <div className="p-6 text-brick">{error || 'Locker room not found.'}</div>

  return (
    <div className="mx-auto max-w-4xl space-y-6 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-cocoa">Public Locker Room Page</h1>
        <p className="text-sm text-cocoa/60">
          Customize what sponsors, parents, and the community see when they visit your locker room.
        </p>
        {publicPageUrl ? (
          <div className="flex items-center gap-2 pt-2">
            <span className="rounded-lg bg-sand/50 px-3 py-1 font-mono text-xs text-cocoa">{publicPageUrl}</span>
            <button
              type="button"
              className="rounded border border-cocoa/20 px-2 py-1 text-xs text-cocoa/70 hover:bg-sand/30"
              onClick={() => navigator.clipboard.writeText(publicPageUrl)}
            >
              Copy
            </button>
            <a href={publicPageUrl} target="_blank" rel="noopener noreferrer" className="rounded border border-cocoa/20 px-2 py-1 text-xs text-cocoa/70 hover:bg-sand/30">
              Preview
            </a>
          </div>
        ) : lockerRoom && !lockerRoom.space_code ? (
          <p className="pt-2 text-xs text-cocoa/50">Public page URL will be available once a space code is generated for your locker room.</p>
        ) : null}
      </header>

      {error && <div className="rounded-xl bg-brick/10 px-4 py-2 text-sm text-brick">{error}</div>}
      {success && <div className="rounded-xl bg-emerald-50 px-4 py-2 text-sm text-emerald-800">{success}</div>}

      {/* Visibility */}
      <section className="space-y-3 rounded-2xl border border-sky-200 bg-sky-50/50 p-5">
        <div>
          <h2 className="font-semibold text-sky-900">Visibility</h2>
          <p className="text-xs text-sky-700">Control who can see your locker room page.</p>
        </div>
        <label className="flex items-start gap-3">
          <input type="checkbox" checked={visibility.isPublic} onChange={(e) => handleVisibilityChange('isPublic', e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300" />
          <div>
            <span className="font-medium text-cocoa">Make locker room public</span>
            <p className="text-xs text-cocoa/60">Anyone with the link can view your locker room page.</p>
          </div>
        </label>
        <label className="flex items-start gap-3">
          <input type="checkbox" checked={visibility.showShareLink} onChange={(e) => handleVisibilityChange('showShareLink', e.target.checked)} disabled={!visibility.isPublic} className="mt-1 h-4 w-4 rounded border-gray-300 disabled:opacity-50" />
          <div>
            <span className="font-medium text-cocoa">Show donate link</span>
            <p className="text-xs text-cocoa/60">Display a donation button on your public page.</p>
          </div>
        </label>
        <label className="flex items-start gap-3">
          <input type="checkbox" checked={visibility.showInCommunityRotation} onChange={(e) => handleVisibilityChange('showInCommunityRotation', e.target.checked)} disabled={!visibility.isPublic} className="mt-1 h-4 w-4 rounded border-gray-300 disabled:opacity-50" />
          <div>
            <span className="font-medium text-cocoa">Appear in community rotation</span>
            <p className="text-xs text-cocoa/60">Feature your locker room in the community discovery feed.</p>
          </div>
        </label>
      </section>

      {/* Display Name & Hero */}
      <section className="space-y-4 rounded-2xl border border-sand/70 bg-white/70 p-5">
        <h2 className="font-semibold text-cocoa">Display Info</h2>
        <label className="block text-sm font-medium text-cocoa">
          Locker Room Name
          <input className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={publicName} onChange={(e) => setPublicName(e.target.value)} placeholder="My Locker Room" />
        </label>
        <label className="block text-sm font-medium text-cocoa">
          Headline
          <input className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={config.hero?.headline || ''} onChange={(e) => setConfig((p) => ({ ...p, hero: { ...p.hero, headline: e.target.value } }))} placeholder="Athletic Development Program" />
        </label>
        <label className="block text-sm font-medium text-cocoa">
          Subheadline
          <input className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={config.hero?.subheadline || ''} onChange={(e) => setConfig((p) => ({ ...p, hero: { ...p.hero, subheadline: e.target.value } }))} placeholder="Building champions on and off the field" />
        </label>
      </section>

      {/* About */}
      <section className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-5">
        <h2 className="font-semibold text-cocoa">About</h2>
        <textarea className="w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" rows={4} value={config.about || ''} onChange={(e) => setConfig((p) => ({ ...p, about: e.target.value }))} placeholder="Tell visitors about your program, coaching philosophy, and what makes your locker room special." />
      </section>

      {/* Quote */}
      <section className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-5">
        <h2 className="font-semibold text-cocoa">Quote / Motto</h2>
        <textarea className="w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" rows={2} value={config.quote?.text || ''} onChange={(e) => handleQuoteChange({ text: e.target.value })} placeholder="Hard work beats talent when talent doesn't work hard." />
        <input className="w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={config.quote?.attribution || ''} onChange={(e) => handleQuoteChange({ attribution: e.target.value })} placeholder="Attribution (e.g., Coach, Team motto)" />
      </section>

      {/* Goals */}
      <section className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-5">
        <h2 className="font-semibold text-cocoa">Season Goals</h2>
        <p className="text-xs text-cocoa/60">What is your team working toward this season?</p>
        <div className="space-y-2">
          {(config.goals || []).map((goal, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cocoa text-xs font-bold text-white">{i + 1}</span>
              <input className="flex-1 rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={goal} onChange={(e) => { const next = [...(config.goals || [])]; next[i] = e.target.value; setConfig((p) => ({ ...p, goals: next })) }} />
              <button type="button" className="text-xs text-brick" onClick={() => setConfig((p) => ({ ...p, goals: (p.goals || []).filter((_, j) => j !== i) }))}>Remove</button>
            </div>
          ))}
        </div>
        <button type="button" className="rounded-xl border border-cocoa px-3 py-1.5 text-xs font-semibold text-cocoa" onClick={() => setConfig((p) => ({ ...p, goals: [...(p.goals || []), ''] }))}>
          Add goal
        </button>
      </section>

      {/* Wishlist */}
      <section className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-5">
        <h2 className="font-semibold text-cocoa">Equipment Wishlist</h2>
        <p className="text-xs text-cocoa/60">Items your program needs. Supporters may help provide these.</p>
        <div className="space-y-3">
          {(config.wishlist || []).map((item, i) => (
            <div key={i} className="grid gap-2 rounded-xl border border-sand/50 bg-white/50 p-3 sm:grid-cols-[1fr,1fr,auto]">
              <input className="rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={item.label} onChange={(e) => handleWishlistChange(i, { label: e.target.value })} placeholder="Resistance bands" />
              <input className="rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={item.url || ''} onChange={(e) => handleWishlistChange(i, { url: e.target.value || null })} placeholder="https://... (optional)" />
              <button type="button" className="text-xs text-brick" onClick={() => setConfig((p) => ({ ...p, wishlist: (p.wishlist || []).filter((_, j) => j !== i) }))}>Remove</button>
            </div>
          ))}
        </div>
        <button type="button" className="rounded-xl border border-cocoa px-3 py-1.5 text-xs font-semibold text-cocoa" onClick={() => setConfig((p) => ({ ...p, wishlist: [...(p.wishlist || []), { label: '' }] }))}>
          Add item
        </button>
      </section>

      {/* Theme */}
      <section className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-5">
        <h2 className="font-semibold text-cocoa">Page Style</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {THEME_OPTIONS.map((opt) => {
            const selected = (config.theme?.style || 'electric') === opt.value
            return (
              <button key={opt.value} type="button" onClick={() => setConfig((p) => ({ ...p, theme: { ...p.theme, style: opt.value as any } }))} className={`flex flex-col rounded-2xl border p-4 text-left transition ${selected ? 'border-cocoa shadow-md' : 'border-sand hover:border-cocoa/60'}`}>
                <span className={`text-sm font-semibold ${selected ? 'text-cocoa' : 'text-cocoa/70'}`}>{opt.label}</span>
                <span className="mt-1 text-xs text-cocoa/50">{opt.desc}</span>
              </button>
            )
          })}
        </div>
        <label className="flex items-center gap-3 pt-2 text-sm font-medium text-cocoa">
          Accent color
          <input type="color" value={config.theme?.accentColor || '#4f46e5'} onChange={(e) => setConfig((p) => ({ ...p, theme: { ...p.theme, accentColor: e.target.value } }))} className="h-8 w-12 cursor-pointer rounded border border-sand" />
          <span className="text-xs text-cocoa/50">{config.theme?.accentColor || '#4f46e5'}</span>
        </label>
      </section>

      {/* Top Moments */}
      <section className="space-y-3 rounded-2xl border border-sand/70 bg-white/70 p-5">
        <h2 className="font-semibold text-cocoa">Top Moments</h2>
        <p className="text-xs text-cocoa/60">Highlight key moments from your season.</p>

        <div className="flex items-center gap-2 text-sm text-cocoa">
          <span>Slots:</span>
          {MOMENT_SLOT_OPTIONS.map((n) => (
            <button key={n} type="button" onClick={() => setConfig((p) => ({ ...p, topMoments: { ...(p.topMoments || defaultTopMoments), maxSlots: n } }))} className={`rounded-full px-3 py-1 text-xs font-semibold ${(config.topMoments?.maxSlots || 5) === n ? 'bg-cocoa text-white' : 'border border-sand text-cocoa'}`}>
              {n}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {(config.topMoments?.items || []).map((moment, i) => (
            <div key={moment.id || i} className="space-y-2 rounded-xl border border-sand/50 bg-white/50 p-4">
              <div className="grid gap-2 sm:grid-cols-2">
                <input className="rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={moment.title} onChange={(e) => handleMomentChange(i, { title: e.target.value })} placeholder="Game-winning play" />
                <input className="rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" value={moment.date || ''} onChange={(e) => handleMomentChange(i, { date: e.target.value })} placeholder="2026-03-15" />
              </div>
              <textarea className="w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none" rows={2} value={moment.summary || ''} onChange={(e) => handleMomentChange(i, { summary: e.target.value })} placeholder="Describe this moment." />
              <div className="flex justify-end">
                <button type="button" className="text-xs text-brick" onClick={() => setConfig((p) => ({ ...p, topMoments: { ...(p.topMoments || defaultTopMoments), items: (p.topMoments?.items || []).filter((_, j) => j !== i) } }))}>Remove</button>
              </div>
            </div>
          ))}
        </div>

        {(config.topMoments?.items || []).length < (config.topMoments?.maxSlots || 5) && (
          <button
            type="button"
            className="rounded-xl border border-cocoa px-3 py-1.5 text-xs font-semibold text-cocoa"
            onClick={() => {
              const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `moment-${Date.now()}`
              const items = [...(config.topMoments?.items || []), { id, title: '', position: (config.topMoments?.items || []).length }]
              setConfig((p) => ({ ...p, topMoments: { ...(p.topMoments || defaultTopMoments), items } }))
            }}
          >
            Add moment
          </button>
        )}
      </section>

      {/* Save + Preview */}
      <div className="flex justify-end gap-3 pb-8">
        {publicPageUrl && (
          <Link
            to={`/locker/${lockerRoom.space_code}`}
            target="_blank"
            className="rounded-xl border border-cocoa/30 px-6 py-3 text-sm font-semibold text-cocoa transition hover:bg-sand/30"
          >
            Preview public page
          </Link>
        )}
        <button type="button" onClick={handleSave} disabled={saving} className="rounded-xl bg-cocoa px-6 py-3 text-sm font-semibold text-white transition hover:bg-cocoa/90 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Public Page'}
        </button>
      </div>
    </div>
  )
}
